import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isIconAccessoryProp } from '#pplx/frontend/aether/components/Accessory/isIconAccessoryProp.js';
import { getChipClasses } from '#pplx/frontend/aether/components/Chip/classes.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Tooltip } from '#pplx/frontend/aether/components/Tooltip/index.js';
import { useAetherLabels } from '#pplx/frontend/aether/internal/labels/useAetherLabels.js';
import { useMeasureableHandle } from '#pplx/frontend/aether/internal/useMeasureableHandle.js';
import { useRadixProps } from '#pplx/frontend/aether/internal/useRadixProps.js';
import { ButtonPrimitive } from '#pplx/frontend/aether/primitives/ButtonPrimitive/index.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import classNames from 'classnames';
import { useCallback } from 'react';
// Crossfade classes used by the delete slot to swap icons on hover/focus.
// CROSSFADE_OUT hides the resting icon; CROSSFADE_IN reveals the X overlay.
const CROSSFADE_OUT = 'group-hover/chip:opacity-0 group-hover/chip:blur-xs keyboard:group-focus-within/chip:opacity-0 keyboard:group-focus-within/chip:blur-xs transition-[opacity,filter] duration-150';
const CROSSFADE_IN = 'absolute inset-0 flex items-center justify-center opacity-0 blur-xs transition-[opacity,filter] duration-150 group-hover/chip:opacity-100 group-hover/chip:blur-0 keyboard:group-focus-within/chip:opacity-100 keyboard:group-focus-within/chip:blur-0';
const suggestionPlusIcon = (_jsx("div", { className: "inline-flex items-center justify-center text-secondary", "aria-hidden": true, children: _jsx(Icon, { icon: $icon('plus'), size: "tiny" }) }));
function MultiSelectIndicator({ checked, variant, }) {
    return (_jsx("span", { "aria-hidden": "true", className: classNames('inline-flex size-4 items-center justify-center rounded-sm border transition-colors duration-quick', checked
            ? variant === 'neutral'
                ? 'border-transparent bg-base/10 text-inverse'
                : 'border-transparent bg-soft text-primary'
            : 'border-base bg-base'), children: checked && _jsx(Icon, { icon: $icon('check'), size: "tiny" }) }));
}
const XIcon = _jsx(Icon, { icon: $icon('x'), size: "tiny", "aria-hidden": true });
const CheckIcon = _jsx(Icon, { icon: $icon('check'), size: "tiny", "aria-hidden": true });
/**
 * Compact interactive chip/tag component used for filters, selections, and compact labels.
 * Supports an optional delete button.
 *
 * @category components
 * @keywords chip, components, compact, interactive, chiptag, component, used, filters, selections,
 *   labels, supports, optional, delete, button, disabled, icon, tooltip, badge
 * @whenToUse Use Chip to represent selectable, filterable, or removable tags in compact UI areas.
 *   Chips are ideal for filter bars, multi-select inputs, tag lists, or any context where users
 *   toggle discrete options on and off. They support selected and disabled states, optional delete
 *   functionality, and modifier key detection for advanced multi-select patterns.
 * @guidelineDo
 *   - Use the selected prop to visually indicate active filter or selection state
 *   - Provide a deleteAriaLabel when using onDelete so the remove button is accessible to screen
 *     readers
 *   - Use the onClick modifier keys (shiftKey, metaKey, ctrlKey) to support multi-select
 *     interactions
 *   - Set shouldAvoidFocusShift to true when chips are rendered inside input containers to prevent
 *     layout disruption
 * @guidelineDont
 *   - Don't use chips for primary actions — they are meant for selection, filtering, or tagging;
 *     use Button for actions like "Apply filters" or "Submit"
 *   - Don't omit the deleteAriaLabel when the generic "Remove" label is insufficient — provide
 *     context-specific labels like "Remove billing filter" for better accessibility
 *   - Don't use disabled chips as a visual-only indicator; disabled chips convey that the option
 *     exists but is unavailable
 *   - Don't render very long labels without a title prop — the chip truncates text, so a title
 *     tooltip provides the full value on hover
 *   - Don't add onDelete to disabled chips — the delete button is automatically hidden when
 *     disabled is true
 *   - Don't use Chip as a non-interactive label — use Badge for read-only status or metadata
 *     annotations that the user cannot dismiss or toggle
 * @accessibility
 *   - Chips render as button elements via ButtonPrimitive, ensuring they are keyboard-focusable and
 *     activatable with Enter or Space
 *   - When a delete button is present, the chip splits into two separate button elements (label and
 *     delete) inside a div container to avoid invalid nested button HTML
 *   - The delete button uses aria-label from the deleteAriaLabel prop and is wrapped in a Tooltip
 *     for sighted users
 *   - Focus ring styling is applied to the container when the label button receives focus-visible,
 *     ensuring a clear keyboard focus indicator
 *   - The shouldAvoidFocusShift prop prevents mousedown from stealing focus, which is critical for
 *     chips used inside input fields
 * @relatedComponents
 *   - icon
 *   - tooltip
 *   - button
 *   - badge
 */
export function Chip({ ref, label, leadingAccessory, isMultiSelect = false, rounded = false, size = 'md', variant = 'neutral', selected = false, checked, onCheckedChange, disabled = false, interactableVariant = 'default', onClick, onDelete, deleteAriaLabel: deleteAriaLabelProp, title, shouldAvoidFocusShift = false, 
// We intentionally capture the rest of props that could be cloned/passed in by 3rd-party libraries
// such as Radix UI for their own internal usage such as data-state, onFocus, onBlur, etc.
...props }) {
    const targetRef = useMeasureableHandle(ref);
    // Manually pick the props that Radix UI passes in for their own internal usage
    // and pass them down to the ButtonPrimitive.
    const __radixProps = useRadixProps(props);
    const labels = useAetherLabels();
    const deleteAriaLabel = deleteAriaLabelProp ?? labels.remove;
    const visuallySelected = checked ?? selected;
    const isSuggestion = variant === 'suggestion';
    // Suggestion chips don't show the selected checkmark — they use their own visual treatment.
    // Multi-select chips render their own indicator, so skip the crossfade-to-check behavior.
    const showSelectedCheck = visuallySelected && !isSuggestion && !isMultiSelect;
    const hasDelete = !!onDelete && !disabled;
    const showDeleteButton = hasDelete;
    // Controls the hover crossfade hint (icon → X on hover).
    const showDeleteHint = hasDelete;
    const chipClasses = getChipClasses({
        disabled,
        rounded,
        size,
        variant,
        visuallySelected,
        // When there's a delete button, it occupies the leading slot even without a user-provided accessory.
        leadingAccessory: isMultiSelect || !!leadingAccessory || showDeleteButton,
    });
    const multiSelectNode = isMultiSelect ? (_jsx(MultiSelectIndicator, { checked: visuallySelected, variant: variant })) : null;
    const handleClick = useCallback((e) => {
        onCheckedChange?.(!visuallySelected);
        onClick?.({
            shiftKey: e.shiftKey,
            metaKey: e.metaKey,
            ctrlKey: e.ctrlKey,
        });
    }, [onClick, onCheckedChange, visuallySelected]);
    const handleDelete = useCallback(() => {
        onDelete?.();
    }, [onDelete]);
    const handleMouseDown = useCallback((e) => {
        if (shouldAvoidFocusShift) {
            // Prevent focus from moving away from the current element (e.g., an input)
            e.preventDefault();
        }
    }, [shouldAvoidFocusShift]);
    const labelContent = (_jsxs(_Fragment, { children: [_jsx("span", { className: "truncate -translate-y-half", children: label }), isSuggestion && !visuallySelected && suggestionPlusIcon] }));
    // When there's a delete action, we use a div container with separate
    // buttons to avoid invalid nested-button HTML.
    if (hasDelete) {
        return (_jsxs("div", { ref: targetRef, ...__radixProps, className: classNames(chipClasses, 'group/chip', interactableVariant === 'alt' &&
                'relative before:absolute before:content-[""] before:pointer-events-none before:rounded-inherit before:inset-2', 
            // The delete variant uses a div (not a button), so CVA hover states don't
            // bubble up. We re-apply hover:bg-subtle via has-[:hover] on the container.
            !disabled && [
                !visuallySelected && !isSuggestion && 'has-[:hover]:bg-subtle',
                interactableVariant === 'alt'
                    ? 'keyboard:has-[.chip-label:focus-visible]:before:outline keyboard:has-[.chip-label:focus-visible]:before:outline-2 keyboard:has-[.chip-label:focus-visible]:before:outline-super-strong keyboard:has-[.chip-label:focus-visible]:before:inset-0.5'
                    : 'keyboard:has-[.chip-label:focus-visible]:outline keyboard:has-[.chip-label:focus-visible]:outline-2 keyboard:has-[.chip-label:focus-visible]:outline-offset-2 keyboard:has-[.chip-label:focus-visible]:outline-super-strong',
            ]), title: title, children: [multiSelectNode && _jsx("span", { className: "p-two inline-flex", children: multiSelectNode }), showDeleteButton && (_jsx(Tooltip, { content: deleteAriaLabel, children: _jsx(ButtonPrimitive, { "aria-label": deleteAriaLabel, onClick: handleDelete, onMouseDown: handleMouseDown, className: classNames('relative inline-flex p-two shrink-0 rounded-full items-center duration-150', isSuggestion && visuallySelected ? 'hover:bg-subtle' : 'hover:bg-base/10'), children: _jsx(DeleteSlotIcon, { leadingAccessory: leadingAccessory, showDeleteHint: showDeleteHint }) }) })), _jsx(ButtonPrimitive, { className: "chip-label !outline-hidden inline-flex min-w-0 items-center gap-1 self-stretch truncate", onClick: disabled ? undefined : handleClick, onMouseDown: handleMouseDown, disabled: disabled, children: labelContent })] }));
    }
    return (_jsxs(ButtonPrimitive, { interactableVariant: interactableVariant === 'alt' ? 'alt' : undefined, ref: targetRef, ...__radixProps, className: chipClasses, onClick: disabled ? undefined : handleClick, onMouseDown: handleMouseDown, disabled: disabled, title: title, children: [(multiSelectNode || leadingAccessory) && (_jsxs("div", { className: "p-two inline-flex items-center gap-1", children: [multiSelectNode, leadingAccessory && (_jsx(LeadingAccessory, { accessory: leadingAccessory, showCheck: showSelectedCheck }))] })), labelContent] }));
}
/**
 * Icon content for the delete button slot. Shows one of three states:
 * - Leading accessory (crossfades to X on hover when `showDeleteHint`)
 * - Static X icon when no accessory is provided
 */
function DeleteSlotIcon({ leadingAccessory, showDeleteHint, }) {
    // The delete button should always show a delete (X) icon — never a checkmark.
    if (leadingAccessory) {
        return (_jsxs(_Fragment, { children: [_jsx(LeadingAccessory, { accessory: leadingAccessory, showCheck: false, className: classNames(showDeleteHint && CROSSFADE_OUT) }), showDeleteHint && _jsx("span", { className: CROSSFADE_IN, children: XIcon })] }));
    }
    return XIcon;
}
/**
 * Renders the leading accessory slot with a crossfade to a checkmark when
 * `showCheck` is true.
 */
function LeadingAccessory({ accessory, showCheck, className, }) {
    const content = isIconAccessoryProp(accessory) ? (_jsx(Icon, { icon: accessory, size: "tiny", "aria-hidden": true })) : (accessory);
    return (_jsxs("span", { className: classNames('relative inline-flex', className), children: [_jsx("span", { className: classNames('inline-flex transition-[opacity,filter] duration-150', showCheck ? 'opacity-0 blur-xs' : 'opacity-100 blur-0'), children: content }), _jsx("span", { className: classNames('absolute inset-0 flex items-center justify-center transition-[opacity,filter] duration-150', showCheck ? 'opacity-100 blur-0' : 'opacity-0 blur-xs'), children: CheckIcon })] }));
}
//# sourceMappingURL=index.js.map